/* F-11 - Accordion behaviour, shared by the USP section and the product info
   section so both behave identically (one of the ticket's acceptance criteria).

   Kept separate from the faq-toggle element in custom.js: that one binds a click
   handler to the whole .faq-block and is shared with the SC FAQ section, so
   changing it would alter pages outside this ticket.

   Behaviour, identical on mobile and desktop:
   - panels open and close independently; opening one does not close any other
   - one panel is open on load (the one marked as active, otherwise the first)
   - panels are real buttons with aria-expanded, so keyboard and screen readers work

   The open/closed height is animated entirely in CSS via grid-template-rows,
   so this script only toggles state. That keeps the animation smooth for any
   content length without measuring anything, and nothing has to be recalculated
   when the viewport or the fonts change.

   Without JS the section renders every panel open (see the noscript rule in the
   sections), so no content is unreachable. 
   Wrapped in an IIFE because both product sections load this file. Two script
   tags with the same src both execute, and a top level class declaration would
   throw "Identifier has already been declared" on the second run. */

(function () {
  class ScProductAccordion extends HTMLElement {
    connectedCallback() {
      // The theme editor can move a section, which disconnects and reconnects the
      // same node. Without this guard each reconnect adds another click handler and
      // one press would then toggle the panel twice, leaving it closed again.
      if (this.initialised) return;

      this.items = Array.from(this.querySelectorAll('[data-sc-accordion-item]'));
      if (this.items.length === 0) return;

      this.initialised = true;

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-sc-accordion-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', () => this.toggle(item));
        trigger.addEventListener('keydown', (event) => this.onKeydown(event));
      });

      const initiallyOpen = this.items.find((item) => item.hasAttribute('data-sc-accordion-open'));

      this.items.forEach((item) => this.setOpen(item, false));
      this.setOpen(initiallyOpen || this.items[0], true);
    }

    // Each item stands on its own: opening one leaves the others untouched, so a
    // shopper can hold size and material side by side instead of losing the first
    // one the moment they open the second.
    toggle(item) {
      this.setOpen(item, !this.isOpen(item));
    }

    isOpen(item) {
      return item.classList.contains('is-open');
    }

    setOpen(item, open) {
      const trigger = item.querySelector('[data-sc-accordion-trigger]');
      const panel = item.querySelector('[data-sc-accordion-panel]');

      item.classList.toggle('is-open', open);
      if (trigger) trigger.setAttribute('aria-expanded', String(open));

      // A collapsed panel keeps its box so the transition has something to animate,
      // which would otherwise leave links inside it tabbable while invisible.
      // inert takes them out of both the tab order and the accessibility tree.
      if (panel) panel.toggleAttribute('inert', !open);
    }

    onKeydown(event) {
      const directions = {
        ArrowDown: 1,
        ArrowUp: -1,
      };

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const target = event.key === 'Home' ? this.items[0] : this.items[this.items.length - 1];
        const trigger = target.querySelector('[data-sc-accordion-trigger]');
        if (trigger) trigger.focus();
        return;
      }

      const direction = directions[event.key];
      if (!direction) return;

      event.preventDefault();

      const current = this.items.findIndex((item) => item.contains(event.target));
      if (current < 0) return;

      const next = (current + direction + this.items.length) % this.items.length;
      const trigger = this.items[next].querySelector('[data-sc-accordion-trigger]');
      if (trigger) trigger.focus();
    }
  }

  if (!customElements.get('sc-product-accordion')) {
    customElements.define('sc-product-accordion', ScProductAccordion);
  }
  })();
